import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Calendar } from "lucide-react";
import { api } from "../lib/api";
import SiteLayout, { SiteHeader, SiteFooter } from "../components/SiteLayout";
import SEO from "../components/SEO";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const CAT_COLOR = {
  "Conceptos básicos": "bg-blue-900/40 text-blue-200 border-blue-700/30",
  "Cargas y deudas": "bg-red-900/40 text-red-200 border-red-700/30",
  "Procesos críticos": "bg-amber-900/40 text-amber-200 border-amber-700/30",
  "Post-subasta": "bg-emerald-900/40 text-emerald-200 border-emerald-700/30",
};

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/blog/posts${filter ? `?category=${encodeURIComponent(filter)}` : ""}`);
        setPosts(data.posts || []);
        setCategories(data.categories || []);
      } finally { setLoading(false); }
    })();
  }, [filter]);

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100">
      <SEO
        title="Blog · Guías sobre subastas BOE | FM Subastas"
        description="Artículos prácticos sobre subastas judiciales del BOE: cargas, embargos, nota simple, cesión de remate, post-subasta. Por Fernando Marcos Subastas & Investment."
        canonical="/blog"
      />
      <SiteHeader />

      <header className="px-6 py-16 sm:py-24 bg-gradient-to-b from-stone-900 to-stone-950">
        <div className="max-w-6xl mx-auto">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#E0651F] mb-4 flex items-center gap-2">
            <BookOpen className="w-4 h-4" /> Blog
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white leading-[1.05] max-w-3xl">
            Guías prácticas sobre <span className="text-[#E0651F]">subastas del BOE</span>.
          </h1>
          <p className="mt-5 text-stone-400 max-w-2xl text-base">
            Artículos directos, sin tecnicismos vacíos. Para que no te juegues tu dinero por no saber.
          </p>
        </div>
      </header>

      <div className="border-y border-stone-800 bg-stone-900/50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex flex-wrap items-center gap-2">
          <button
            onClick={() => setFilter("")}
            data-testid="blog-filter-all"
            className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-bold rounded-sm border transition-colors ${!filter ? "bg-[#E0651F] text-white border-[#E0651F]" : "bg-transparent text-stone-400 border-stone-700 hover:text-white"}`}>
            Todas
          </button>
          {categories.map((c) => (
            <button key={c} onClick={() => setFilter(c)}
              data-testid={`blog-filter-${c.replace(/\s/g, "-").toLowerCase()}`}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-bold rounded-sm border transition-colors ${filter === c ? "bg-[#E0651F] text-white border-[#E0651F]" : "bg-transparent text-stone-400 border-stone-700 hover:text-white"}`}>
              {c}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-6 py-12">
        {loading ? (
          <div className="text-center py-24 text-stone-500">Cargando…</div>
        ) : posts.length === 0 ? (
          <div className="text-center py-24 text-stone-500">
            <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-40" />
            Próximamente — estamos preparando contenido.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`}
                data-testid={`blog-post-${p.slug}`}
                className="group bg-stone-900 border border-stone-800 hover:border-[#E0651F]/50 transition-colors rounded-sm overflow-hidden flex flex-col">
                <div className="aspect-[16/9] bg-gradient-to-br from-stone-800 to-stone-900 overflow-hidden">
                  {p.cover_image_url ? (
                    <img
                      src={p.cover_image_url.startsWith("http") ? p.cover_image_url : `${API_BASE}${p.cover_image_url}`}
                      alt={p.cover_image_alt || p.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-6xl">{p.cover_emoji || "📄"}</div>
                  )}
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className={`inline-block self-start text-[10px] uppercase tracking-wide font-bold px-2 py-1 rounded-sm border mb-3 ${CAT_COLOR[p.category] || "bg-stone-700 text-stone-200 border-stone-600"}`}>
                    {p.category}
                  </div>
                  <h3 className="font-display font-black text-lg text-white mb-2 leading-snug group-hover:text-[#ff7a30] transition-colors line-clamp-3">
                    {p.title}
                  </h3>
                  <p className="text-sm text-stone-400 line-clamp-3 mb-4">{p.excerpt}</p>
                  <div className="mt-auto flex items-center justify-between text-xs text-stone-500">
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(p.created_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" })}</span>
                    <span className="inline-flex items-center gap-1 text-[#E0651F] font-bold uppercase tracking-wide">Leer <ArrowRight className="w-3 h-3" /></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}
